import { isUnicode } from '@/shared/utils/string';
import {
  defaultTextEditorFont,
  UnicodeMap,
  UnicodeRegular,
} from '@/shared/utils/text-editor';
import { Editor } from '@tiptap/react';
import { DOMSerializer } from 'prosemirror-model';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  selectInitialMessageMedia,
  selectInitialMessageOptOutText,
} from '../../Jobs/redux/jobEditors.selectors';
import { JobTemplateMedia } from '../../Jobs/types';
import { TextEditorFont, TextEditorOperation } from '../types';
import { getEditorPlainText as getEditorPlainTextUtil, transformPTags } from '../utils';

// Define the type for the optional context
interface TextEditorContext {
  media?: JobTemplateMedia | null;
  jobId?: string; // Add optional jobId to context
  optOutMessage?: string; // NEW: Allow direct opt-out override
  useJobsRedux?: boolean; // NEW: Flag to control Redux usage (defaults to true)
}

const useTextEditor = (
  editor: Editor | null,
  context?: TextEditorContext // Add optional context argument
) => {
  const excludeCustomNodes = ['TEXT-EDITOR-ATTRIBUTE'];
  // Prioritize context media, fallback to Redux
  const contextMedia = context?.media;
  const jobId = context?.jobId || ''; // Default to empty string for new jobs
  const useJobsRedux = context?.useJobsRedux !== false; // Default to true
  const reduxMedia = useSelector(selectInitialMessageMedia(jobId));
  const mediaSource = contextMedia ?? reduxMedia;

  const [editorPlainText, setEditorPlainText] = useState<string>('');
  const [editorContent, setEditorContent] = useState<string>('');
  const isMessageMms = !!mediaSource; // Determine MMS based on the resolved media source
  const isMessageUnicode = isUnicode(editorPlainText);

  // Conditional opt-out logic: use direct override or fall back to Jobs Redux
  const jobsReduxOptOut = useSelector(selectInitialMessageOptOutText(jobId));
  const optOutMessage = context?.optOutMessage ?? (useJobsRedux ? jobsReduxOptOut : '');

  const identifyOperationsFromText = (text: string) => {
    const chars = Array.from(text); // Converts the string into an array of characters
    const char = chars[0];

    let result: string | undefined;

    UnicodeMap.forEach((characters, key) => {
      if (characters.includes(char)) {
        result = key;
      }
    });

    if (result) {
      return {
        isBold: result.includes('-bold'),
        isItalics: result.includes('-italic'),
        fontName: result.replace('-italic', '').replace('-bold', ''),
      };
    }

    return {
      isBold: false,
      isItalics: false,
      fontName: defaultTextEditorFont.name,
    };
  };

  const getBaseLetters = (selection: string): string => {
    let w = '';
    const iterator = selection[Symbol.iterator]();
    let result = iterator.next();

    while (!result.done) {
      let c = result.value;
      let found = false;

      UnicodeMap.forEach((values) => {
        if (!values.includes(c)) {
          return;
        }

        w += UnicodeRegular[values.indexOf(c)];
        c = ''; // Clear the character since it's already mapped
        found = true;
      });

      // If the character wasn't found in the UnicodeMap, add it as is
      if (!found) {
        w += c;
      }

      result = iterator.next();
    }

    return w;
  };

  const getAlteredLetters = (selection: string, fmt: string): string => {
    let w = '';
    const fmtmap = UnicodeMap.get(fmt);

    if (!fmtmap) {
      return selection; // Return unchanged if format not found
    }

    for (let t = 0; t < selection.length; t++) {
      const c = selection[t];

      const index = UnicodeRegular.indexOf(c);
      if (index !== -1) {
        w += fmtmap[index]; // Use character from format map if found
      } else {
        w += c; // Otherwise, append the character as is
      }
    }

    return w;
  };

  const transformFontLetters = (text: string, fontName: string) => {
    const textWithoutEndingNewLineChar = text.replace(/\n$/, '');
    const baseLetters = getBaseLetters(textWithoutEndingNewLineChar);
    const alteredLetters = getAlteredLetters(baseLetters, fontName);
    return alteredLetters;
  };

  // Function to get selected HTML
  const getEditorSelectedState = () => {
    if (!editor) {
      return {
        selectedHtml: '',
        from: null,
        to: null,
      };
    }

    const { state } = editor;
    const { selection } = state;

    // Check if there's an active selection
    if (selection.empty) {
      return {
        selectedHtml: '',
        from: null,
        to: null,
      };
    }

    // Extract the selected content as a slice
    const slice = selection.content();

    // Convert the selected content slice to DOM and then to HTML
    const fragment = DOMSerializer.fromSchema(editor.schema).serializeFragment(
      slice.content
    );

    // Create a temporary container to hold the HTML
    const temporaryDiv = document.createElement('div');
    temporaryDiv.appendChild(fragment);

    return {
      selectedHtml: transformPTags(temporaryDiv.innerHTML),
      from: selection.from,
      to: selection.to,
    };
  };

  // Function to prepare content to return
  useEffect(() => {
    if (!editor) {
      return;
    }

    const updateListener = ({ editor }: { editor: Editor }) => {
      const message = getEditorPlainTextUtil(editor, optOutMessage);
      setEditorPlainText(message);
      setEditorContent(editor.getHTML());
    };

    // Call once as optout message may have changed
    updateListener({ editor });

    // Listen to both update and transaction events to catch all content changes
    editor.on('update', updateListener);
    editor.on('transaction', updateListener);

    return () => {
      editor.off('update', updateListener);
      editor.off('transaction', updateListener);
    };
  }, [editor, optOutMessage]);

  // Function to apply transformations based on selected text
  const applyFormattingToSelection = (
    fontName?: TextEditorFont['name'],
    operations?: TextEditorOperation[]
  ) => {
    if (!editor) {
      return;
    }

    let selectedFontName = fontName;
    const isBold = operations?.includes('bold');
    const isItalics = operations?.includes('italics');

    if ((!fontName || fontName === 'regular') && (isBold || isItalics)) {
      selectedFontName = 'sans-serif';
    }

    const finalFontName = `${selectedFontName}${isBold ? '-bold' : ''}${isItalics ? '-italic' : ''}`;

    // Get the selected HTML
    const { selectedHtml, from } = getEditorSelectedState();

    // Function to process the selected HTML
    const transformContent = (html: string) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      // Traverse through child nodes
      const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_ALL);
      const nodesToTransform: { node: Text; parent: Node }[] = [];

      while (walker.nextNode()) {
        const currentNode = walker.currentNode;

        if (currentNode.nodeType === Node.TEXT_NODE) {
          const parentNode = currentNode.parentNode;

          // Check if parent node should not be transformed
          if (parentNode && !excludeCustomNodes.includes(parentNode.nodeName)) {
            nodesToTransform.push({ node: currentNode as Text, parent: parentNode });
          }
        }
      }

      // Transform text to uppercase for collected nodes
      nodesToTransform.forEach(({ node, parent }) => {
        const transformedText = transformFontLetters(
          node.textContent || '',
          finalFontName
        );
        const newTextNode = document.createTextNode(transformedText);

        // Replace the old text node with the new one
        parent.replaceChild(newTextNode, node);
      });

      // Return the modified HTML
      return tempDiv.innerHTML;
    };

    // Process selected HTML
    const updatedContent = transformContent(selectedHtml);

    // Replace the selected text in the editor with updated content
    replaceSelectedText(updatedContent);

    // Reset the selection based on new characters
    if (from) {
      const to = from + updatedContent.length;
      editor.commands.setTextSelection({ from, to });
    }
  };

  // Function to replace selected text
  const replaceSelectedText = (newText: string) => {
    if (!editor) return;

    editor.chain().focus().insertContent(newText, { updateSelection: true }).run();
  };

  // Function to set text
  const setContent = (blockContent: string, inlineContent?: string) => {
    if (!editor) return;

    // The opt-out span is inserted inline directly after the body, so without
    // separating whitespace the body text and opt-out run together visually
    // (e.g. "message bodyStop to opt out"). If the opt-out doesn't already
    // start with whitespace, prepend a space.
    const normalizedInlineContent =
      inlineContent && !/^\s/.test(inlineContent) ? ` ${inlineContent}` : inlineContent;

    editor
      .chain()
      .clearContent()
      .insertContent(blockContent, { updateSelection: true })
      .insertContent(
        normalizedInlineContent
          ? {
              type: 'span',
              content: [{ type: 'text', text: normalizedInlineContent }],
            }
          : '',
        { updateSelection: true }
      )
      .run();
  };

  return {
    identifyOperationsFromText,
    applyFormattingToSelection,
    getEditorSelectedState,
    replaceSelectedText,
    setContent,
    isMessageMms,
    isMessageUnicode,
    isUnicode,
    editorPlainText, // This already includes the opt-out message
    editorContent,
    optOutMessage, // Expose opt-out for reference
  };
};

export default useTextEditor;
